/*
PURPOSE: Custom hook for managing notification data, loading states, and user interactions

USE CASES:
- Fetch user notifications with pagination support
- Track read/unread status based on timestamp comparison
- Handle "mark all as viewed" functionality with optimistic updates
- Manage loading and error states for notification operations
- Provide real-time refresh capabilities for active users

DOMAIN BOUNDARIES:
- Responsible for notification state management and API integration
- Does NOT handle UI rendering or component concerns
- Does NOT manage global app state beyond notifications
- Integrates with existing API endpoints: getUserNotifications, markNotificationsAsViewed

ASSUMPTIONS:
- User is authenticated with contributor access level
- API endpoints return paginated results with proper interfaces
- Timestamp-based approach for determining read/unread status
- Optimistic updates for better user experience
*/

import { useState, useEffect, useCallback } from 'react';
import { NotificationEntity } from 'shared/types/platformTypes';
import { 
  getUserNotifications, 
  markNotificationsAsViewed,
  GetNotificationsRequest,
  GetNotificationsResponse 
} from '../api/api';
import { useAuthContext } from '../components/Auth/AuthContext/AuthContext';
import { useIsContributor } from './useAccessControl';

interface UseNotificationsReturn {
  notifications: NotificationEntity[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  lastViewedAt?: string;
  loadMoreNotifications: () => Promise<void>;
  markAllAsViewed: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

export const useNotifications = (): UseNotificationsReturn => {
  const { userProfile } = useAuthContext();
  const isContributor = useIsContributor();
  
  // State management
  const [notifications, setNotifications] = useState<NotificationEntity[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [lastKey, setLastKey] = useState<string | undefined>(undefined);
  const [lastViewedAt, setLastViewedAt] = useState<string | undefined>(undefined);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async (
    append = false, 
    paginationKey?: string
  ): Promise<void> => {
    if (!userProfile || !isContributor) return;

    try {
      setError(null);
      if (!append) setIsLoading(true);

      const request: GetNotificationsRequest = {
        limit: 20,
        ...(paginationKey && { lastKey: paginationKey })
      };

      const response: GetNotificationsResponse = await getUserNotifications(request);

      setNotifications(prev => 
        append ? [...prev, ...response.notifications] : response.notifications
      );
      
      setUnreadCount(response.unreadCount);
      setHasMore(response.pagination.hasMore);
      setLastKey(response.pagination.lastKey);
      setLastViewedAt(response.lastViewedAt);
      setHasInitialLoad(true);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
      console.error('Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userProfile, isContributor]);

  // Load more notifications (pagination)
  const loadMoreNotifications = useCallback(async (): Promise<void> => {
    if (!hasMore || isLoading) return;
    await fetchNotifications(true, lastKey);
  }, [fetchNotifications, hasMore, isLoading, lastKey]);

  // Mark all notifications as viewed
  const markAllAsViewed = useCallback(async (): Promise<void> => {
    if (!userProfile || !isContributor || unreadCount === 0) return;

    try {
      const currentTime = new Date().toISOString();
      
      // Optimistic update
      setUnreadCount(0);
      setLastViewedAt(currentTime);

      // API call
      await markNotificationsAsViewed({ viewedAt: currentTime });

    } catch (err) {
      // Revert optimistic update on error
      setUnreadCount(prev => prev); // This will trigger a re-fetch
      setError('Failed to mark notifications as viewed');
      console.error('Failed to mark notifications as viewed:', err);
    }
  }, [userProfile, isContributor, unreadCount]);

  // Refresh notifications (for real-time updates)
  const refreshNotifications = useCallback(async (): Promise<void> => {
    await fetchNotifications(false);
  }, [fetchNotifications]);

  // Initial load
  useEffect(() => {
    if (userProfile && isContributor) {
      fetchNotifications(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, isContributor]);

  // Auto-refresh when page becomes visible (basic real-time functionality)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && userProfile && isContributor) {
        refreshNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, isContributor]);

  // Periodic refresh for active users (every 30 seconds)
  useEffect(() => {
    if (!userProfile || !isContributor) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        refreshNotifications();
      }
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, isContributor]);

  return {
    notifications,
    unreadCount: hasInitialLoad ? unreadCount : 0,
    isLoading,
    error,
    hasMore,
    lastViewedAt,
    loadMoreNotifications,
    markAllAsViewed,
    refreshNotifications
  };
};